import React from "react";
import { ContactList } from "./ContactList";
import type { Database } from "@/lib/supabase";
import ScrollArea from "../ui/shared/ScrollArea";
import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  applyChatContactScopeFilter,
  canSeeChatOwnershipTabUnassigned,
} from "@/lib/chatContactScope";
import { dedupeAccountContacts } from "@/utils/contactUtils";
import { ChatFilters, type ChatFilterValues, defaultChatFilters } from "./ChatFilters";
import { useTranslation } from "react-i18next";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface ChatSidebarProps {
  contacts: AccountContact[];
  selectedContact: AccountContact | null;
  onContactSelect: (contact: AccountContact) => void;
  loading: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  filters: ChatFilterValues;
  onFiltersChange: (filters: ChatFilterValues) => void;
}

const StatusToggle = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (status: string) => void;
}) => {
  const { t } = useTranslation("chat");
  return (
    <div className="flex items-center gap-2 h-[32px]">
      <span className="text-[26px] font-bold text-foreground dark:text-neutral-50">{t("area.chats")}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-card border border-border text-foreground text-[11px] font-medium rounded-[12px] px-1.5 py-0.5 outline-none cursor-pointer dark:bg-neutral-700 dark:border-neutral-500 dark:text-neutral-50"
      >
        <option value="aberto">{t("area.open")}</option>
        <option value="concluido">{t("area.closed")}</option>
      </select>
    </div>
  );
};

type OwnershipValue = "__mine__" | "__all__" | "__unassigned__";

const OwnershipToggle = ({
  value,
  onChange,
  counts,
  showUnassignedTab = true,
}: {
  value: OwnershipValue;
  onChange: (v: OwnershipValue) => void;
  counts?: Record<OwnershipValue, number>;
  showUnassignedTab?: boolean;
}) => {
  const { t } = useTranslation("chat");
  const items: { id: OwnershipValue; label: string }[] = [
    { id: "__mine__", label: "Meus" },
    { id: "__all__", label: "Todos" },
    { id: "__unassigned__", label: "Não atribuídos" },
  ];

  const visibleItems = items.filter((item) => {
    if (item.id === "__unassigned__" && !showUnassignedTab) return false;
    return true;
  });

  return (
    <div className="relative flex flex-col">
      <div className="flex gap-3 pb-2">
        {visibleItems.map((item) => {
          const isSelected = value === item.id;
          const count = counts?.[item.id];
          return (
            <button
              key={item.id}
              onClick={() => onChange(item.id)}
              className={`relative text-[11px] font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-1 ${
                isSelected
                  ? "text-[#2563EB] dark:text-[#60A5FA]"
                  : "text-muted-foreground hover:text-foreground dark:text-neutral-400 dark:hover:text-neutral-200"
              }`}
            >
              {item.label}
              {count !== undefined && (
                <span className={`text-[10px] rounded px-1 py-px ${
                  isSelected
                    ? "bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]"
                    : "bg-[#EEF4FF] text-[#2563EB] dark:bg-[#27272a] dark:text-neutral-400"
                }`}>
                  {count}
                </span>
              )}
              {isSelected && (
                <span className="absolute -bottom-2 left-0 right-0 h-[2px] bg-[#2563EB] dark:bg-[#2563EB] z-10" />
              )}
            </button>
          );
        })}
      </div>
      <div className="h-[2px] bg-border dark:bg-[#27272a]" />
    </div>
  );
};

const SidebarHeader = ({
  searchTerm,
  onSearchChange,
  filters,
  onFiltersChange,
  currentUserId,
  ownershipCounts,
  showUnassignedOwnershipTab = true,
}: {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filters: ChatFilterValues;
  onFiltersChange: (filters: ChatFilterValues) => void;
  currentUserId?: string;
  ownershipCounts?: Record<OwnershipValue, number>;
  showUnassignedOwnershipTab?: boolean;
}) => {
  const { t } = useTranslation("chat");
  const handleStatusChange = (status: string) => {
    onFiltersChange({ ...filters, statusChat: status });
  };

  const ownershipValue: OwnershipValue =
    filters.attributionId === "__unassigned__"
      ? "__unassigned__"
      : filters.attributionId && filters.attributionId === currentUserId
        ? "__mine__"
        : "__all__";

  const handleOwnershipChange = (v: OwnershipValue) => {
    let attributionId: string | null = null;
    if (v === "__mine__" && currentUserId) {
      attributionId = currentUserId;
    } else if (v === "__unassigned__") {
      attributionId = "__unassigned__";
    }
    onFiltersChange({ ...filters, attributionId });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <StatusToggle
        value={filters.statusChat || "aberto"}
        onChange={handleStatusChange}
      />
      <OwnershipToggle
        value={ownershipValue}
        onChange={handleOwnershipChange}
        counts={ownershipCounts}
        showUnassignedTab={showUnassignedOwnershipTab}
      />
      <div className="flex gap-2">
        <div className="gap-2 rounded-[12px] border border-border bg-card px-3 flex justify-start items-center flex-1 h-[36px] shadow-sm transition-all duration-200 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 dark:border-neutral-500 dark:bg-neutral-600 dark:shadow-none dark:focus-within:ring-0">
          <svg className="w-[16px] h-[16px] flex-shrink-0 text-muted-foreground" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6.91382 2.65576L7.15698 2.67822C8.76918 2.87399 10.1267 4.08856 10.5281 5.64697L10.5955 5.96338C10.8047 7.21398 10.4233 8.38828 9.68433 9.25049L9.48511 9.48193L9.25366 9.68115C8.39132 10.4203 7.21639 10.8018 5.96558 10.5923H5.96655C4.36514 10.3215 3.06273 9.04828 2.73413 7.47314L2.6814 7.15381C2.3712 4.63045 4.41754 2.49568 6.91382 2.65576ZM6.65112 3.31396C4.80703 3.31396 3.31714 4.80385 3.31714 6.64795C3.31726 8.49194 4.80711 9.98096 6.65112 9.98096C8.49503 9.98083 9.984 8.49186 9.98413 6.64795C9.98413 4.80393 8.49511 3.31409 6.65112 3.31396ZM9.98413 9.98682L9.89038 9.89014L9.81909 9.81592L9.89331 9.88721L9.98999 9.98096H10.5125L13.241 12.7231H13.2419C13.3851 12.8663 13.3851 13.1024 13.2419 13.2456C13.0988 13.3887 12.8626 13.3887 12.7195 13.2456L9.98413 10.5093V9.98682Z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="0.666667"
            />
          </svg>
          <input
            type="text"
            placeholder={t("area.searchContacts")}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="grow-1 text-[13px] font-normal text-foreground placeholder:text-muted-foreground w-full h-full border-none focus:outline-none bg-transparent dark:text-neutral-50 dark:placeholder:text-neutral-400"
          />
        </div>
        <ChatFilters filters={filters} onFiltersChange={onFiltersChange} />
      </div>
    </div>
  );
};

export const ChatSidebar = ({
  contacts,
  selectedContact,
  onContactSelect,
  loading,
  hasMore = false,
  onLoadMore,
  filters,
  onFiltersChange,
}: ChatSidebarProps) => {
  const { t } = useTranslation("chat");
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<AccountContact[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [ownershipCounts, setOwnershipCounts] = useState<Record<OwnershipValue, number>>({
    __mine__: 0,
    __all__: 0,
    __unassigned__: 0,
  });

  // Os contadores das abas são três COUNT(*) exatos. Antes eles dependiam da identidade do
  // array `contacts`, que muda a cada mensagem que chega em qualquer conversa da conta — em
  // conta movimentada isso era uma rajada de contagens, vezes o número de abas abertas.
  //
  // Agora a atividade da lista só agenda uma recontagem, e uma rajada inteira vira uma
  // chamada só. Trocar de filtro continua recontando na hora, porque aí o número na tela
  // ficaria errado. Mantemos 'exact': é número que o usuário lê, estimativa não serve.
  const COUNTS_COALESCE_MS = 3000;
  const [countsTick, setCountsTick] = useState(0);
  const countsTimerRef = useRef<number | null>(null);
  const countsPrimedRef = useRef(false);

  useEffect(() => {
    // A primeira passada é a montagem; o efeito de contagem já busca por conta própria.
    if (!countsPrimedRef.current) {
      countsPrimedRef.current = true;
      return;
    }
    if (countsTimerRef.current !== null) return; // já agendado, a rajada se junta neste

    countsTimerRef.current = window.setTimeout(() => {
      countsTimerRef.current = null;
      setCountsTick((tick) => tick + 1);
    }, COUNTS_COALESCE_MS);
  }, [contacts]);

  useEffect(() => () => {
    if (countsTimerRef.current !== null) window.clearTimeout(countsTimerRef.current);
  }, []);

  useEffect(() => {
    if (!user?.account_id || !supabase) return;

    const fetchCounts = async () => {
      if (!supabase) return;
      const sb = supabase;
      const statusFilter = filters.statusChat || "aberto";
      const tagFilter = filters.tag;

      const buildQuery = (ownership: OwnershipValue) => {
        let q = sb
          .from("account_contacts")
          .select("*", { count: "exact", head: true })
          .eq("account_id", user.account_id!);

        q = applyChatContactScopeFilter(q, user);

        q = q.eq("status_chat", statusFilter);

        if (tagFilter) {
          q = q.contains("tags", [tagFilter.toLowerCase()]);
        }

        if (ownership === "__mine__" && user.auth_user_id) {
          q = q.eq("attribution_id", user.auth_user_id);
        } else if (ownership === "__unassigned__") {
          q = q.is("attribution_id", null);
        }

        return q;
      };

      const [mine, all, unassigned] = await Promise.all([
        buildQuery("__mine__"),
        buildQuery("__all__"),
        buildQuery("__unassigned__"),
      ]);

      setOwnershipCounts({
        __mine__: mine.count ?? 0,
        __all__: all.count ?? 0,
        __unassigned__: unassigned.count ?? 0,
      });
    };

    fetchCounts();
    // `contacts` saiu daqui de propósito — quem representa a atividade da lista agora é
    // `countsTick`, que já vem coalescido pelo efeito acima.
  }, [user, user?.account_id, user?.auth_user_id, filters.statusChat, filters.tag, countsTick]);

  const searchContacts = useCallback(async (term: string) => {
    if (!user?.account_id || !supabase || !term.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearching(true);

      const searchPattern = `%${term}%`;

      let query = applyChatContactScopeFilter(
        supabase
          .from('account_contacts')
          .select('*')
          .eq('account_id', user.account_id),
        user
      ).or(`display_name.ilike.${searchPattern},push_name_last.ilike.${searchPattern},jid.ilike.${searchPattern},phone.ilike.${searchPattern},email.ilike.${searchPattern}`);

      if (filters.attributionId) {
        if (filters.attributionId === '__unassigned__') {
          query = query.is('attribution_id', null);
        } else {
          query = query.eq('attribution_id', filters.attributionId);
        }
      }

      if (filters.statusChat) {
        query = query.eq('status_chat', filters.statusChat);
      }

      if (filters.tag) {
        query = query.contains('tags', [filters.tag.toLowerCase()]);
      }

      const { data: contactsData, error: contactsError } = await query
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(50);

      if (contactsError) throw contactsError;

      setSearchResults(dedupeAccountContacts(contactsData || []));
    } catch (err: any) {
      console.error('Erro ao buscar contatos:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [user, user?.account_id, filters]);

  // Debounce para busca - aguarda 300ms após o usuário parar de digitar
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchTerm.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchContacts(searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchContacts]);

  // Determinar quais contatos mostrar: resultados da busca ou contatos paginados
  const displayedContacts = isSearching ? searchResults : contacts;
  const isLoading = isSearching ? searchLoading : loading;
  const shouldShowInfiniteScroll = !isSearching && hasMore;

  // Intersection Observer para infinite scroll (apenas quando não estiver pesquisando)
  React.useEffect(() => {
    if (!onLoadMore || !shouldShowInfiniteScroll || isLoading) return;

    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          console.log('📜 [ChatSidebar] Loading more contacts...');
          onLoadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    observer.observe(currentTarget);

    return () => {
      try {
        if (currentTarget && observer) {
          observer.unobserve(currentTarget);
        }
        observer.disconnect();
      } catch (error) {
        console.warn('⚠️ [ChatSidebar] Error disconnecting observer:', error);
      }
    };
  }, [onLoadMore, shouldShowInfiniteScroll, hasMore, isLoading]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const showUnassignedOwnershipTab = canSeeChatOwnershipTabUnassigned(user);

  return (
    <div className="flex gap-2 flex-col h-full min-w-[280px]">
      <SidebarHeader
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFiltersChange={onFiltersChange}
        currentUserId={user?.auth_user_id || undefined}
        ownershipCounts={ownershipCounts}
        showUnassignedOwnershipTab={showUnassignedOwnershipTab}
      />
      <ScrollArea
        classNames={{
          root: "flex-1 md:block hidden h-full w-full overflow-auto md:max-w-[280px]",
        }}
      >
        <ContactList
          contacts={displayedContacts}
          selectedContact={selectedContact}
          onContactSelect={onContactSelect}
          loading={isLoading}
          searchTerm={searchTerm}
        />
        {shouldShowInfiniteScroll && (
          <div ref={observerTarget} className="py-4 text-center">
            <div className="text-sm text-neutral-400">{t("area.loadingMore")}</div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
